import * as SCTheme from 'constants/sc-theme';
import {PaletteDark, PaletteLight} from 'mui/palette';
import shadows from 'mui/shadows';

import type {PaletteOptions} from '@material-ui/core/styles/createPalette';
import type {TypographyOptions} from '@material-ui/core/styles/createTypography';
import type {ComponentNameToClassKey, Overrides} from '@material-ui/core/styles/overrides';
import type {StyleRules} from '@material-ui/core/styles/withStyles';

const inputBorderRadius = '0.4rem';
const inputHeight = '3.6rem';
const listItemHeight = '3rem';

//#region Mui Button

const getButtonOverridesLight = () => ({
    containedPrimary: {
        color: PaletteLight.common.white,
        backgroundColor: PaletteLight.primary['500'],
        '&:active': {
            backgroundColor: PaletteLight.primary['700'],
        },
        '&:disabled': {
            color: PaletteLight.grey['400'],
            backgroundColor: PaletteLight.grey['100'],
        },
        '&:focus:not(:active)': {
            backgroundColor: PaletteLight.primary['400'],
        },
        '&:hover': {
            backgroundColor: PaletteLight.primary['400'],
        },
    },
    containedSecondary: {
        color: PaletteLight.common.white,
        backgroundColor: PaletteLight.error['500'],
        '&:active': {
            backgroundColor: PaletteLight.error['800'],
        },
        '&:disabled': {
            color: PaletteLight.grey['400'],
            backgroundColor: PaletteLight.grey['100'],
        },
        '&:focus:not(:active)': {
            backgroundColor: PaletteLight.error['700'],
        },
        '&:hover': {
            backgroundColor: PaletteLight.error['700'],
        },
    },
    outlinedPrimary: {
        color: PaletteLight.primary['500'],
        borderColor: PaletteLight.primary['500'],
        backgroundColor: PaletteLight.common.white,
        '&:active': {
            color: PaletteLight.primary['700'],
            borderColor: PaletteLight.primary['700'],
            backgroundColor: PaletteLight.common.white,
        },
        '&:focus:not(:active)': {
            color: PaletteLight.primary['400'],
            borderColor: PaletteLight.primary['400'],
            backgroundColor: PaletteLight.common.white,
        },
        '&:hover': {
            color: PaletteLight.primary['400'],
            borderColor: PaletteLight.primary['400'],
            backgroundColor: PaletteLight.common.white,
        },
    },
    outlinedSecondary: {
        color: PaletteLight.error['500'],
        borderColor: PaletteLight.error['500'],
        backgroundColor: PaletteLight.common.white,
        '&:active': {
            color: PaletteLight.error['700'],
            borderColor: PaletteLight.error['700'],
            backgroundColor: PaletteLight.common.white,
        },
        '&:hover': {
            color: PaletteLight.error['400'],
            borderColor: PaletteLight.error['400'],
            backgroundColor: PaletteLight.common.white,
        },
        '&:focus:not(:active)': {
            color: PaletteLight.error['400'],
            borderColor: PaletteLight.error['400'],
            backgroundColor: PaletteLight.common.white,
        },
    },
});

const MuiButtonOverridesLight = (
    typographyOptions: TypographyOptions,
): Partial<StyleRules<ComponentNameToClassKey['MuiButton']>> => {
    const buttonOverrides = getButtonOverridesLight();

    return {
        sizeSmall: {
            height: SCTheme.SIZE_SMALL_HEIGHT,
            fontSize: typographyOptions.componentS.fontSize,
            lineHeight: typographyOptions.componentS.lineHeight,
            borderRadius: '0.4rem',
        },
        sizeLarge: {
            height: SCTheme.SIZE_LARGE_HEIGHT,
            fontSize: typographyOptions.componentM.fontSize,
            lineHeight: typographyOptions.componentM.lineHeight,
            fontWeight: typographyOptions.fontWeightBold as number,
            borderRadius: '0.8rem',
        },
        root: {
            height: SCTheme.SIZE_MEDIUM_HEIGHT,
            minWidth: '6.4rem',
            width: 'fit-content',
            fontSize: typographyOptions.componentM.fontSize,
            lineHeight: typographyOptions.componentM.lineHeight,
            fontWeight: typographyOptions.fontWeightBold as number,
            whiteSpace: 'nowrap',
            borderRadius: '0.6rem',
            // Default transition with 'color' added to match 'background-color'
            transition:
                'color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            '&$disabled': {
                color: PaletteLight.grey['400'],
                borderColor: PaletteLight.grey['100'],
            },
        },
        // text buttons are styled like links
        text: {
            border: 'none',
            color: PaletteLight.primary['500'],

            '&:hover': {
                background: 'none',
                textDecoration: 'underline',
            },
        },
        textPrimary: {
            color: PaletteLight.primary['500'],

            '&:active': {
                backgroundColor: PaletteLight.primary['100'],

                color: PaletteLight.primary['700'],
            },
            '&:focus:not(:active)': {
                backgroundColor: PaletteLight.primary['20'],
            },
            '&:hover': {
                backgroundColor: PaletteLight.primary['20'],
                textDecoration: 'none',
            },
        },
        textSecondary: {
            color: PaletteLight.error['500'],
            '&:active': {
                backgroundColor: PaletteLight.error['100'],

                color: PaletteLight.error['700'],
            },
            '&:focus:not(:active)': {
                backgroundColor: PaletteLight.error['20'],
            },
            '&:hover': {
                backgroundColor: PaletteLight.error['20'],
                textDecoration: 'none',
            },
        },
        ...buttonOverrides,
        textSizeSmall: typographyOptions.componentXs,
    };
};

const getButtonOverridesDark = () => ({
    containedPrimary: {
        color: PaletteDark.primary['400'],
        backgroundColor: PaletteDark.common.white,
        '&:active': {
            backgroundColor: PaletteDark.primary['200'],
            color: PaletteDark.primary['700'],
        },
        '&:disabled': {
            color: PaletteDark.primary['800'],
            backgroundColor: PaletteDark.primary['700'],
        },
        '&:focus:not(:active)': {
            backgroundColor: PaletteDark.primary['100'],
        },
        '&:hover': {
            backgroundColor: PaletteDark.primary['100'],
            color: PaletteDark.primary['500'],
        },
    },
    containedSecondary: {
        color: PaletteDark.error['400'],
        backgroundColor: PaletteDark.common.white,
        '&:active': {
            backgroundColor: PaletteDark.error['200'],
            color: PaletteDark.error['700'],
        },
        '&:disabled': {
            color: PaletteDark.error['800'],
            backgroundColor: PaletteDark.error['700'],
        },
        '&:focus:not(:active)': {
            backgroundColor: PaletteDark.error['100'],
        },
        '&:hover': {
            backgroundColor: PaletteDark.error['100'],
            color: PaletteDark.error['500'],
        },
    },
    outlinedPrimary: {
        color: PaletteDark.common.white,
        borderColor: PaletteDark.common.white,
        backgroundColor: 'none',
        '&:active': {
            color: PaletteDark.primary['300'],
            borderColor: PaletteDark.primary['300'],
            backgroundColor: 'none',
        },
        '&:disabled': {
            color: PaletteDark.primary['700'],
            borderColor: PaletteDark.primary['700'],
            backgroundColor: 'none',
        },
        '&:focus:not(:active)': {
            color: PaletteDark.primary['200'],
            borderColor: PaletteDark.primary['200'],
            backgroundColor: 'none',
        },
        '&:hover': {
            color: PaletteDark.primary['200'],
            borderColor: PaletteDark.primary['200'],
            background: 'none',
            backgroundColor: 'none',
        },
    },
    outlinedSecondary: {
        color: PaletteDark.common.white,
        borderColor: PaletteDark.common.white,
        backgroundColor: 'none',
        '&:active': {
            color: PaletteDark.error['300'],
            borderColor: PaletteDark.error['300'],
            backgroundColor: 'none',
        },
        '&:disabled': {
            color: PaletteDark.error['700'],
            borderColor: PaletteDark.error['700'],
            backgroundColor: 'none',
        },
        '&:focus:not(:active)': {
            color: PaletteDark.error['200'],
            borderColor: PaletteDark.error['200'],
            backgroundColor: 'none',
        },
        '&:hover': {
            color: PaletteDark.error['200'],
            borderColor: PaletteDark.error['200'],
            background: 'none',
            backgroundColor: 'none',
        },
    },
});

const MuiButtonOverridesDark = (
    typographyOptions: TypographyOptions,
): Partial<StyleRules<ComponentNameToClassKey['MuiButton']>> => {
    const buttonOverrides = getButtonOverridesDark();

    return {
        sizeSmall: {
            height: SCTheme.SIZE_SMALL_HEIGHT,
            fontSize: typographyOptions.componentS.fontSize,
            lineHeight: typographyOptions.componentS.lineHeight,
            borderRadius: '0.4rem',
        },
        sizeLarge: {
            height: SCTheme.SIZE_LARGE_HEIGHT,
            fontSize: typographyOptions.componentM.fontSize,
            lineHeight: typographyOptions.componentM.lineHeight,
            fontWeight: typographyOptions.fontWeightBold as number,
            borderRadius: '0.8rem',
        },
        root: {
            height: SCTheme.SIZE_MEDIUM_HEIGHT,
            minWidth: '6.4rem',
            width: 'fit-content',
            fontSize: typographyOptions.componentM.fontSize,
            lineHeight: typographyOptions.componentM.lineHeight,
            fontWeight: typographyOptions.fontWeightBold as number,
            whiteSpace: 'nowrap',
            borderRadius: '0.6rem',
            // Default transition with 'color' added to match 'background-color'
            transition:
                'color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            '&$disabled': {
                color: PaletteDark.primary['400'],

                borderColor: PaletteDark.primary['400'],
            },
        },
        // text buttons are styled like links
        text: {
            border: 'none',
            color: PaletteDark.primary['400'],
            '&:hover': {
                background: 'none',
                textDecoration: 'underline',
            },
        },
        textPrimary: {
            color: PaletteDark.primary['400'],

            '&:active': {
                backgroundColor: PaletteDark.primary['500'],
                color: PaletteDark.common.white,
            },
            '&:disabled': {
                color: PaletteDark.primary['700'],
            },
            '&:focus:not(:active)': {
                color: PaletteDark.primary['200'],
                backgroundColor: PaletteDark.primary['600'],
            },
            '&:hover': {
                color: PaletteDark.primary['200'],
                backgroundColor: PaletteDark.primary['600'],
                textDecoration: 'none',
            },
        },
        textSecondary: {
            color: PaletteDark.error['400'],
            '&:active': {
                backgroundColor: PaletteDark.error['500'],
                color: PaletteDark.common.white,
            },
            '&:disabled': {
                color: PaletteDark.error['700'],
            },
            '&:focus:not(:active)': {
                color: PaletteDark.error['200'],
                backgroundColor: PaletteDark.error['600'],
            },
            '&:hover': {
                color: PaletteDark.error['200'],
                backgroundColor: PaletteDark.error['600'],
                textDecoration: 'none',
            },
        },
        ...buttonOverrides,
        textSizeSmall: typographyOptions.componentXs,
    };
};

//#endregion

//#region Mui Icon Button

const MuiIconButtonOverridesDark: Partial<StyleRules<ComponentNameToClassKey['MuiIconButton']>> = {
    root: {
        color: PaletteDark.common.white,
        '&$disabled': {
            color: PaletteDark.primary['700'],
        },
    },
    colorPrimary: {
        color: PaletteDark.common.white,
    },
    colorSecondary: {
        color: PaletteDark.common.white,
    },
};

//#endregion

const getOverrides = (typographyOptions: TypographyOptions, paletteOptions: PaletteOptions): Overrides => {
    const buttonOverrides = paletteOptions.type === 'light' ? getButtonOverridesLight() : getButtonOverridesDark();

    return {
        MuiBackdrop: {
            root: {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
            },
        },
        MuiButton:
            paletteOptions.type === 'light'
                ? MuiButtonOverridesLight(typographyOptions)
                : MuiButtonOverridesDark(typographyOptions),
        MuiButtonGroup: {
            contained: {
                boxShadow: 'none',
            },
            groupedContainedPrimary: buttonOverrides.containedPrimary,
            groupedContainedSecondary: buttonOverrides.containedSecondary,
            groupedOutlined: {
                backgroundColor: PaletteLight.common.white,
                color: PaletteLight.primary['500'],
                borderColor: PaletteLight.primary['500'],
                '&:hover': {
                    color: PaletteLight.primary['400'],
                    borderColor: PaletteLight.primary['400'],
                    backgroundColor: PaletteLight.common.white,
                },
                '&:disabled': {
                    color: PaletteLight.grey['400'],
                    borderColor: PaletteLight.grey['100'],
                },
            },
            groupedOutlinedPrimary: buttonOverrides.outlinedPrimary,
            groupedOutlinedSecondary: buttonOverrides.outlinedSecondary,
        },
        MuiCheckbox: {
            colorPrimary: {
                '&.MuiCheckbox-indeterminate:not($disabled)': {
                    color: PaletteLight.primary['500'],
                },
                '&$disabled': {
                    color: PaletteLight.grey['500'],
                },
            },
            colorSecondary: {
                '&.MuiCheckbox-indeterminate:not($disabled)': {
                    color: PaletteLight.secondary['500'],
                },
                '&$disabled': {
                    color: PaletteLight.grey['500'],
                },
            },
            root: {
                '&:not(.Mui-checked):not($disabled)': {
                    color: PaletteLight.text.primary,
                },
                '&$disabled': {
                    color: PaletteLight.grey['500'],
                },
            },
        },
        MuiChip: {
            root: {
                ...typographyOptions.componentXs,
                borderRadius: '24px',
                fontWeight: typographyOptions.fontWeightBold as number,
            },
        },
        MuiIconButton: paletteOptions.type === 'light' ? undefined : MuiIconButtonOverridesDark,
        MuiInputBase: {
            input: {
                color: PaletteLight.text.secondary,
                '&::placeholder': {
                    ...typographyOptions.componentItalicS,
                    color: PaletteLight.text.hint,
                    opacity: 1,
                    transition: 'none',
                },
            },
        },
        MuiInputLabel: {
            root: {
                ...typographyOptions.componentUppercaseXs,
                paddingBottom: 8,
            },
        },
        MuiMenu: {
            list: {
                paddingTop: 5,
                paddingBottom: 5,
                boxShadow: shadows[1],
            },
        },
        MuiMenuItem: {
            root: {
                lineHeight: typographyOptions.componentS.lineHeight,
                height: listItemHeight,
                '&:hover': {
                    backgroundColor: PaletteLight.grey['100'],
                },
            },
        },
        MuiPaper: {
            outlined: {
                border: `1px solid ${PaletteLight.grey['200']}`,
            },
            rounded: {
                borderRadius: '0.5rem',
            },
        },
        MuiSelect: {
            root: {
                borderRadius: inputBorderRadius,
                cursor: 'pointer',
                height: inputHeight,
            },
        },
        MuiSvgIcon: {
            colorAction: {
                color: PaletteLight.warning['400'],
            },
            root: {
                fontSize: '2.4rem',
            },
        },
        MuiTableBody: {
            root: {
                '& > tr:nth-child(odd)': {
                    backgroundColor: PaletteLight.grey['20'],
                },
            },
        },
        MuiTableHead: {
            root: {
                backgroundColor: PaletteLight.grey['100'],
            },
        },
        MuiTooltip: {
            tooltip: {
                fontSize: typographyOptions.componentXs.fontSize,
                lineHeight: typographyOptions.componentXs.lineHeight,
                color: PaletteLight.text.primary,
                backgroundColor: PaletteLight.common.white,
                borderRadius: '0.5rem',
                padding: '1rem',
                boxShadow: shadows[1],
                borderColor: PaletteLight.grey['100'],
                borderStyle: 'solid',
                borderWidth: '0.2rem',
                maxWidth: '28rem',
                whiteSpace: 'break-spaces',
            },
            arrow: {
                color: PaletteLight.common.white,
                fontSize: typographyOptions.componentXl.fontSize,

                '&:before': {
                    boxShadow: shadows[1],
                    borderColor: PaletteLight.grey['100'],
                    borderStyle: 'solid',
                    borderWidth: '0.2rem',
                    boxSizing: 'border-box',
                },
            },
        },
        MuiTypography: {
            colorError: {
                color: PaletteLight.error['500'],
            },
        },
    };
};

export default getOverrides;
